import {
    Button,
    ButtonColor,
    ButtonType,
    Container,
    ContainerSize,
    Form,
    FormControlType,
    FormGroup,
    FormLabel,
    Heading,
    InputGroup,
    InputGroupSize,
    InputGroupText,
    Text,
    Wrapper,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const InputGroupPage = () => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Input group
            </Heading>
            <Text className="mb-4">
                Extend text inputs, selects, and file inputs with text, buttons, or other controls on either side.
                Behavior follows{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/forms/input-group/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap’s input group documentation
                </Wrapper>
                . Use <Text el="code">InputGroup</Text> and <Text el="code">InputGroupText</Text>, or the
                same on <Text el="code">Form</Text> as <Text el="code">Form.InputGroup</Text> and{" "}
                <Text el="code">Form.InputGroupText</Text>.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Basic example
            </Heading>
            <Text className="mb-4">
                Place add-ons on the left, right, or both sides of a control. Keep <Text el="code">&lt;label&gt;</Text>s
                outside the group; use <Text el="code">Form.Text</Text> for help below the field.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <InputGroupText id="ig-basic-1">@</InputGroupText>
                        <Form.Control
                            placeholder="Username"
                            aria-label="Username"
                            aria-describedby="ig-basic-1"
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder="Recipient's username"
                            aria-label="Recipient's username"
                            aria-describedby="ig-basic-2"
                        />
                        <InputGroupText id="ig-basic-2">@example.com</InputGroupText>
                    </InputGroup>
                    <FormGroup className="mb-3">
                        <FormLabel htmlFor="ig-basic-url">Your vanity URL</FormLabel>
                        <InputGroup>
                            <InputGroupText id="ig-basic-3">https://example.com/users/</InputGroupText>
                            <Form.Control
                                id="ig-basic-url"
                                aria-describedby="ig-basic-3 ig-basic-4"
                            />
                        </InputGroup>
                        <Form.Text id="ig-basic-4">Example help text goes outside the input group.</Form.Text>
                    </FormGroup>
                    <InputGroup className="mb-3">
                        <InputGroupText>$</InputGroupText>
                        <Form.Control aria-label="Amount (to the nearest dollar)" />
                        <InputGroupText>.00</InputGroupText>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Control placeholder="Username" aria-label="Username" />
                        <InputGroupText>@</InputGroupText>
                        <Form.Control placeholder="Server" aria-label="Server" />
                    </InputGroup>
                    <InputGroup>
                        <InputGroupText>With textarea</InputGroupText>
                        <Form.Control el="textarea" aria-label="With textarea" />
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {basicExampleCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Wrapping
            </Heading>
            <Text className="mb-4">
                Groups wrap by default. Set <Text el="code">nowrap</Text> on <Text el="code">InputGroup</Text> to apply{" "}
                <Text el="code">flex-nowrap</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup nowrap className="mb-3">
                        <InputGroupText id="ig-nowrap">@</InputGroupText>
                        <Form.Control
                            placeholder="Username"
                            aria-label="Username"
                            aria-describedby="ig-nowrap"
                        />
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {wrappingCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">size</Text> on <Text el="code">InputGroup</Text> with{" "}
                <Text el="code">InputGroupSize.Small</Text> or <Text el="code">InputGroupSize.Large</Text>. Do not repeat
                control size classes on each child.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup size={InputGroupSize.Small} className="mb-3">
                        <InputGroupText id="ig-sz-sm">Small</InputGroupText>
                        <Form.Control aria-label="Sizing example input" aria-describedby="ig-sz-sm" />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroupText id="ig-sz-def">Default</InputGroupText>
                        <Form.Control aria-label="Sizing example input" aria-describedby="ig-sz-def" />
                    </InputGroup>
                    <InputGroup size={InputGroupSize.Large}>
                        <InputGroupText id="ig-sz-lg">Large</InputGroupText>
                        <Form.Control aria-label="Sizing example input" aria-describedby="ig-sz-lg" />
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {sizingCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Checkboxes and radios
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">InputGroupText</Text> with <Text el="code">el=&quot;div&quot;</Text> and a plain{" "}
                <Text el="code">form-check-input mt-0</Text> input, as in Bootstrap’s examples.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <InputGroupText el="div">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                value=""
                                aria-label="Checkbox for following text input"
                            />
                        </InputGroupText>
                        <Form.Control aria-label="Text input with checkbox" />
                    </InputGroup>
                    <InputGroup>
                        <InputGroupText el="div">
                            <input
                                className="form-check-input mt-0"
                                type="radio"
                                value=""
                                aria-label="Radio button for following text input"
                            />
                        </InputGroupText>
                        <Form.Control aria-label="Text input with radio button" />
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {checkRadioCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Multiple inputs
            </Heading>
            <Text className="mb-4">
                Multiple inputs are supported visually; validation styling applies only when there is a single input in the group.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup>
                        <InputGroupText>First and last name</InputGroupText>
                        <Form.Control aria-label="First name" />
                        <Form.Control aria-label="Last name" />
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {multipleInputsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Multiple add-ons
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <InputGroupText>$</InputGroupText>
                        <InputGroupText>0.00</InputGroupText>
                        <Form.Control aria-label="Dollar amount (with dot and two decimal places)" />
                    </InputGroup>
                    <InputGroup>
                        <Form.Control aria-label="Dollar amount (with dot and two decimal places)" />
                        <InputGroupText>$</InputGroupText>
                        <InputGroupText>0.00</InputGroupText>
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {multipleAddonsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Button add-ons
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <Button
                            color={ButtonColor.OutlineSecondary}
                            type={ButtonType.Button}
                            id="ig-btn-1"
                        >
                            Button
                        </Button>
                        <Form.Control
                            placeholder=""
                            aria-label="Example text with button addon"
                            aria-describedby="ig-btn-1"
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder="Recipient's username"
                            aria-label="Recipient's username"
                            aria-describedby="ig-btn-2"
                        />
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button} id="ig-btn-2">
                            Button
                        </Button>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                        <Form.Control placeholder="" aria-label="Example text with two button addons" />
                    </InputGroup>
                    <InputGroup>
                        <Form.Control
                            placeholder="Recipient's username"
                            aria-label="Recipient's username with two button addons"
                        />
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {buttonAddonsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom select
            </Heading>
            <Text className="mb-4">
                Pair <Text el="code">Form.Select</Text> with <Text el="code">InputGroupText</Text> as a{" "}
                <Text el="code">label</Text> when matching Bootstrap’s markup.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <InputGroupText el="label" htmlFor="ig-select-1">
                            Options
                        </InputGroupText>
                        <Form.Select id="ig-select-1" defaultValue="">
                            <option value="">Choose...</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Form.Select>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Select id="ig-select-2" defaultValue="">
                            <option value="">Choose...</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Form.Select>
                        <InputGroupText el="label" htmlFor="ig-select-2">
                            Options
                        </InputGroupText>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                        <Form.Select id="ig-select-3" aria-label="Example select with button addon" defaultValue="">
                            <option value="">Choose...</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Form.Select>
                    </InputGroup>
                    <InputGroup>
                        <Form.Select id="ig-select-4" aria-label="Example select with button addon" defaultValue="">
                            <option value="">Choose...</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Form.Select>
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button}>
                            Button
                        </Button>
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {customSelectCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom file input
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <InputGroup className="mb-3">
                        <InputGroupText el="label" htmlFor="ig-file-1">
                            Upload
                        </InputGroupText>
                        <Form.Control type={FormControlType.File} id="ig-file-1" />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Control type={FormControlType.File} id="ig-file-2" />
                        <InputGroupText el="label" htmlFor="ig-file-2">
                            Upload
                        </InputGroupText>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button} id="ig-file-btn-3">
                            Button
                        </Button>
                        <Form.Control
                            type={FormControlType.File}
                            id="ig-file-3"
                            aria-describedby="ig-file-btn-3"
                            aria-label="Upload"
                        />
                    </InputGroup>
                    <InputGroup>
                        <Form.Control
                            type={FormControlType.File}
                            id="ig-file-4"
                            aria-describedby="ig-file-btn-4"
                            aria-label="Upload"
                        />
                        <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button} id="ig-file-btn-4">
                            Button
                        </Button>
                    </InputGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {fileInputCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

const basicExampleCode = `<InputGroup className="mb-3">
    <InputGroupText id="ig-basic-1">@</InputGroupText>
    <Form.Control
        placeholder="Username"
        aria-label="Username"
        aria-describedby="ig-basic-1"
    />
</InputGroup>
{/* …addon right, URL + Form.Text, $ .00, two inputs, textarea… */}`;

const wrappingCode = `<InputGroup nowrap className="mb-3">
    <InputGroupText id="ig-nowrap">@</InputGroupText>
    <Form.Control
        placeholder="Username"
        aria-label="Username"
        aria-describedby="ig-nowrap"
    />
</InputGroup>`;

const sizingCode = `<InputGroup size={InputGroupSize.Small} className="mb-3">
    <InputGroupText id="ig-sz-sm">Small</InputGroupText>
    <Form.Control aria-label="Sizing example input" aria-describedby="ig-sz-sm" />
</InputGroup>
<InputGroup className="mb-3">…</InputGroup>
<InputGroup size={InputGroupSize.Large}>…</InputGroup>`;

const checkRadioCode = `<InputGroup className="mb-3">
    <InputGroupText el="div">
        <input
            className="form-check-input mt-0"
            type="checkbox"
            value=""
            aria-label="Checkbox for following text input"
        />
    </InputGroupText>
    <Form.Control aria-label="Text input with checkbox" />
</InputGroup>`;

const multipleInputsCode = `<InputGroup>
    <InputGroupText>First and last name</InputGroupText>
    <Form.Control aria-label="First name" />
    <Form.Control aria-label="Last name" />
</InputGroup>`;

const multipleAddonsCode = `<InputGroup className="mb-3">
    <InputGroupText>$</InputGroupText>
    <InputGroupText>0.00</InputGroupText>
    <Form.Control aria-label="Dollar amount (with dot and two decimal places)" />
</InputGroup>`;

const buttonAddonsCode = `<InputGroup className="mb-3">
    <Button color={ButtonColor.OutlineSecondary} type={ButtonType.Button} id="ig-btn-1">
        Button
    </Button>
    <Form.Control aria-label="Example text with button addon" aria-describedby="ig-btn-1" />
</InputGroup>`;

const customSelectCode = `<InputGroup className="mb-3">
    <InputGroupText el="label" htmlFor="ig-select-1">
        Options
    </InputGroupText>
    <Form.Select id="ig-select-1">…</Form.Select>
</InputGroup>`;

const fileInputCode = `<InputGroup className="mb-3">
    <InputGroupText el="label" htmlFor="ig-file-1">
        Upload
    </InputGroupText>
    <Form.Control type={FormControlType.File} id="ig-file-1" />
</InputGroup>`;

export default InputGroupPage;
